import { ChangeContactInfoService } from '@pulseops/submission/common'
import { TaskDetail, GeneralService, Panel, TransactionType, SubmissionService } from '@pulseops/common'
import { SourceType } from '@pulseops/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { InfoViewList } from '../common'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import { ErrorHandling } from '@pulseops/common'
import { TFunctionResult } from 'i18next'
import { Text, StyleSheet, View, ActivityIndicator } from 'react-native'
import { useLoading } from '@mxt/zio-react'
import * as O from 'fp-ts/Option'

type DataSource = {
  label: string | number | boolean | JSX.Element | TFunctionResult
  value: string | number | boolean | JSX.Element
  suffix?: string | number | boolean | JSX.Element
  isHighlight?: boolean
}

export const ChangeContactInfoAddress = ({ detail: gDetail }: { detail?: TaskDetail.ChangePrimaryAddressInfo }) => {
  const { t } = useTranslation()
  const [isLoading, bindLoading] = useLoading(false)

  const defaultCity = '08' // HCM
  const defaultDistrict = '01'

  let data: DataSource[] = []
  let priorPolicies: { policyNumber: string; address: string }[] = []

  const getClientAddressInfoFromLAS = (policyNumber: string, isDigital?: boolean) => {
    return isDigital ?
      pipe(
        SubmissionService.getPolicyInquiry(policyNumber),
        ZIO.map((customerInfo) => {
          const street = pipe(
            O.fromNullable(customerInfo?.customerRoles),
            O.map((roles) => roles.length > 0 ? roles[0].customer.addressDetails.Current.line1 : ''),
            O.getOrElse(() => '')
          )
          const nationality = pipe(
            O.fromNullable(customerInfo?.customerRoles),
            O.map((roles) => roles.length > 0 ? roles[0].customer.addressDetails.Current.country : ''),
            O.getOrElse(() => '')
          )
          const province = pipe(
            O.fromNullable(customerInfo?.customerRoles),
            O.map((roles) => roles.length > 0 ? roles[0].customer.addressDetails.Current?.line2 || '' : ''),
            O.getOrElse(() => '')
          )
          const district = pipe(
            O.fromNullable(customerInfo?.customerRoles),
            O.map((roles) => roles.length > 0 ? roles[0].customer.addressDetails.Current?.line3 || '' : ''),
            O.getOrElse(() => '')
          )
          const ward = pipe(
            O.fromNullable(customerInfo?.customerRoles),
            O.map((roles) => roles.length > 0 ? roles[0].customer.addressDetails.Current?.line4 || '' : ''),
            O.getOrElse(() => '')
          )
          return { nationality, province, district, ward, street }
        })
      ) :
      pipe(
        SubmissionService.getPolicy(policyNumber),
        ZIO.flatMap((policyInfo) => {
          return SubmissionService.getCustomer(policyInfo.body.clientDespatchAddress || '')
        }),
        ZIO.map((customerInfo) => {
          let street = '', nationality = '', province = '', district = '', ward = ''
          const hasNewProvinceCode = pipe(
            O.fromNullable(customerInfo?.body.location),
            O.fold(
              () => false,
              (locationItem) => locationItem.startsWith('Z') ? true : false
            )
          )
          const LASNewProvinceCode = pipe(
            customerInfo?.body.location,
            O.fromNullable,
            O.fold(
              () => '',
              (locationItem) => locationItem.startsWith('Z') ? locationItem.slice(0, 3) : ''
            )
          )
          const LASNewWardCode = pipe(
            customerInfo?.body.location,
            O.fromNullable,
            O.fold(
              () => '',
              (locationItem) => locationItem.startsWith('Z') ? locationItem.slice(3) : ''
            )
          )
          street = customerInfo?.body ? customerInfo?.body?.addressDetails?.PRIMARY?.line1 ?? '' : ''
          nationality = customerInfo?.body.nationality ?? ''
          province = hasNewProvinceCode
            ? LASNewProvinceCode
            : pipe(
              O.fromNullable(customerInfo?.body.location),
              O.fold(
                () => '',
                (locationCode) => locationCode.slice(0, 2)
              )
            )
          // district = customerInfo?.body ? customerInfo?.body.location.slice(2, 4) ?? '' : ''
          district = hasNewProvinceCode
            ? ''
            : pipe(
              O.fromNullable(customerInfo?.body.location),
              O.fold(
                () => '',
                (locationCode) => locationCode.slice(2, 4)
              )
            )
          // ward = customerInfo?.body ? customerInfo?.body.location ?? '' : ''
          ward = hasNewProvinceCode ? LASNewWardCode : pipe(
            O.fromNullable(customerInfo?.body.location),
            O.fold(
              () => '',
              (locationCode) => locationCode
            )
          )
          return { nationality, province, district, ward, street }
        })
      )
  }

  if (gDetail?.detail.source === SourceType.PULSE4OPS
    || gDetail?.detail.source === SourceType.BANCA_SEA
    || gDetail?.detail.source === SourceType.OUTBOUND
    || gDetail?.detail.source === SourceType.INBOUND
    || gDetail?.detail.source === SourceType.PRUSERVICES
  ) {
    const { district, city, country, ward } = gDetail.detail
    const isPSDigital =
      gDetail?.detail.source === SourceType.BANCA_SEA &&
      gDetail?.tag === TransactionType.CHANGE_CONTACT_INFO_ADDRESS_DIGITAL
    const isNewProvinceFlag = district === ''
    const { initialDetail, countries, provinces, districts, wards, newProvinceList, newWardList } = pipe(
      ZIO.zipPar(
        getClientAddressInfoFromLAS(gDetail.policyNo, isPSDigital),
        GeneralService.getCountries,
        !isNewProvinceFlag ? GeneralService.getProvinces : ZIO.succeed([]),
        !isNewProvinceFlag ? GeneralService.getDistricts(city ?? defaultCity) : ZIO.succeed([]),
        !isNewProvinceFlag ? GeneralService.getWards({
          provinceCode: city ?? defaultCity,
          districtCode: district ?? defaultDistrict
        }) : ZIO.succeed([]),
        isNewProvinceFlag ? GeneralService.getNewProvinceList : ZIO.succeed([]),
        isNewProvinceFlag ? GeneralService.getNewWardList(city) : ZIO.succeed([])
      ),
      ZIO.map(([initialDetail, countries, provinces, districts, wards, newProvinceList, newWardList]) => {
        return { initialDetail, countries, provinces, districts, wards, newProvinceList, newWardList }
      }),
      bindLoading,
      ErrorHandling.runDidMount()
    ) || { detail: null, countries: [], provinces: [], districts: [], wards: [], newProvinceList: [], newWardList: [] }

    const countryName: string = countries.find((c) => country === c.code)?.name ?? '-'
    const cityName: string = isNewProvinceFlag ? newProvinceList.find((x) => x.code === city)?.name || '-' : provinces.find((c) => c.code === city)?.name ?? '-'
    const districtName: string = isNewProvinceFlag ? '' : districts.find((c) => c.code === district)?.name ?? '-'
    const wardName: string = isNewProvinceFlag ? newWardList.find((x) => x.code === ward)?.name || '-' : wards.find((c) => c.code === ward)?.name ?? '-'

    priorPolicies = gDetail.detail.priorPolicies || []
    data = [
      {
        label: t('requestInfo:Country'),
        value: countryName,
        isHighlight: country !== initialDetail?.nationality
      },
      {
        label: t('contact:CityProvince'),
        value: cityName,
        isHighlight: city !== initialDetail?.province
      }
    ]
    if (!isNewProvinceFlag) {
      data.push({
        label: t('requestInfo:District'),
        value: districtName,
        isHighlight: district !== initialDetail?.district
      })
    }
    data = [...data, ...[{
      label: t('contact:WardSubDistrict'),
      value: wardName,
      isHighlight: ward !== initialDetail?.ward
    },
    {
      label: t('contact:Address'),
      value: gDetail.detail.street,
      isHighlight: gDetail.detail.street !== initialDetail?.street
    }]]
  } else if (gDetail?.detail.source === SourceType.PULSE) {
    const { district, city, country, ward } = gDetail.detail
    const { detail, countries } = pipe(
      ZIO.zipPar(
        getClientAddressInfoFromLAS(gDetail.policyNo),
        GeneralService.getCountries
      ),
      ZIO.map(([detail, countries]) => {
        return { detail, countries }
      }),
      bindLoading,
      ErrorHandling.runDidMount()
    ) || { detail: null, countries: [], provinces: [], districts: [], wards: [] }

    const countryName: string = countries.find((c) => country === c.code)?.name || '-'
    const cityName: string = city || '-'
    const districtName: string = district || '-'
    const wardName: string = ward || '-'

    data = [
      {
        label: t('requestInfo:Country'),
        value: countryName,
        isHighlight: country !== detail?.nationality
      },
      {
        label: t('contact:CityProvince'),
        value: cityName,
        isHighlight: city !== detail?.province
      },
      {
        label: t('requestInfo:District'),
        value: districtName,
        isHighlight: city !== detail?.district
      },
      {
        label: t('contact:WardSubDistrict'),
        value: wardName,
        isHighlight: city !== detail?.ward
      },
      {
        label: t('contact:Address'),
        value: gDetail.detail.street,
        isHighlight: gDetail.detail.street !== detail?.street
      }
    ]
  } else {
    data = [
      {
        label: t('contact:Address'),
        value: gDetail?.detail.source || '-'
      }
    ]
  }



  return isLoading ? (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
      <ActivityIndicator size="large" color="#ED1B2E" />
    </View>
  ) : (
    <Panel title={t('requestInfo:ChangeAddress')} isExand={false}>
      <InfoViewList dataSource={data} />
      {priorPolicies.length > 0 && (
        <>
          <Text style={styles.label}>{t('requestInfo:UpdatedForOtherPolicy')}</Text>
          {priorPolicies.map(({ policyNumber, address }, index) => (
            <Text key={index}>{`${policyNumber} ${address}`}</Text>
          ))}
        </>
      )}
    </Panel>
  )
}

const styles = StyleSheet.create({
  label: {
    color: '#70777E',
    fontSize: 15,
    fontWeight: 'bold',
    lineHeight: 20,
    marginBottom: 8
  }
})
